/**
 * Reviews Queries
 * Functions for lawyer reviews and ratings system
 */

import { supabase } from '../supabase-client';

export interface CreateReviewInput {
  lawyerId: string;
  clientId: string;
  rating: number;
  comment?: string;
  consultationId?: string;
}

export interface Review {
  id: string;
  lawyer_id: string;
  client_id: string;
  consultation_id?: string | null;
  rating: number;
  comment: string | null;
  helpful_count: number;
  is_verified: boolean;
  created_at: string;
  updated_at: string;
  client?: {
    full_name: string;
    profile_image_url: string | null;
  };
}

export interface RatingBreakdown {
  rating: number;
  count: number;
  percentage: number;
}

/**
 * Create a new review for a lawyer
 */
export async function createReview(input: CreateReviewInput): Promise<Review> {
  const { lawyerId, clientId, rating, comment, consultationId } = input;

  const { data, error } = await supabase
    .from('lawyer_reviews')
    .insert({
      lawyer_id: lawyerId,
      client_id: clientId,
      rating,
      comment: comment || null,
      consultation_id: consultationId || null,
      is_verified: !!consultationId, // Verified if linked to consultation
    })
    .select('*')
    .single();

  if (error) {
    if (error.code === '23505') {
      // Unique constraint violation
      throw new Error('You have already reviewed this lawyer for this consultation');
    }
    throw new Error(`Error creating review: ${error.message}`);
  }

  return {
    ...data,
    helpful_count: data.helpful_count || 0,
    comment: data.comment || null,
    consultation_id: data.consultation_id || null,
    is_verified: data.is_verified || false,
    created_at: data.created_at || new Date().toISOString(),
    updated_at: data.updated_at || new Date().toISOString(),
  };
}

/**
 * Get all reviews for a lawyer
 */
export async function getLawyerReviews(
  lawyerId: string,
  options?: {
    limit?: number;
    offset?: number;
    sortBy?: 'recent' | 'helpful' | 'rating';
  }
): Promise<Review[]> {
  let query = supabase
    .from('lawyer_reviews')
    .select(`
      *,
      client:profiles!client_id(
        full_name,
        profile_image_url
      )
    `)
    .eq('lawyer_id', lawyerId);

  // Sorting
  switch (options?.sortBy) {
    case 'helpful':
      query = query.order('helpful_count', { ascending: false });
      break;
    case 'rating':
      query = query.order('rating', { ascending: false });
      break;
    default:
      query = query.order('created_at', { ascending: false });
  }

  // Pagination
  if (options?.limit) {
    query = query.limit(options.limit);
  }
  if (options?.offset) {
    query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(`Error fetching reviews: ${error.message}`);
  }

  return (data || []).map(review => {
    const result: any = {
      ...review,
      helpful_count: review.helpful_count || 0,
      comment: review.comment || null,
      consultation_id: review.consultation_id || null,
      is_verified: review.is_verified || false,
      created_at: review.created_at || new Date().toISOString(),
      updated_at: review.updated_at || new Date().toISOString(),
    };
    if (review.client) {
      result.client = {
        full_name: review.client.full_name || 'Anonymous',
        profile_image_url: review.client.profile_image_url || null,
      };
    }
    return result;
  }) as Review[];
}

/**
 * Get a single review by ID
 */
export async function getReview(reviewId: string): Promise<Review | null> {
  const { data, error } = await supabase
    .from('lawyer_reviews')
    .select(`
      *,
      client:profiles!client_id(
        full_name,
        profile_image_url
      )
    `)
    .eq('id', reviewId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw new Error(`Error fetching review: ${error.message}`);
  }

  if (!data) return null;
  
  const result: any = {
    ...data,
    helpful_count: data.helpful_count || 0,
    comment: data.comment || null,
    consultation_id: data.consultation_id || null,
    is_verified: data.is_verified || false,
    created_at: data.created_at || new Date().toISOString(),
    updated_at: data.updated_at || new Date().toISOString(),
  };
  if (data.client) {
    result.client = {
      full_name: data.client.full_name || 'Anonymous',
      profile_image_url: data.client.profile_image_url || null,
    };
  }
  return result;
}

/**
 * Update a review
 */
export async function updateReview(
  reviewId: string,
  updates: { rating?: number; comment?: string }
): Promise<Review> {
  const { data, error } = await supabase
    .from('lawyer_reviews')
    .update({
      ...(updates.rating !== undefined && { rating: updates.rating }),
      ...(updates.comment !== undefined && { comment: updates.comment }),
    })
    .eq('id', reviewId)
    .select('*')
    .single();

  if (error) {
    throw new Error(`Error updating review: ${error.message}`);
  }

  return {
    ...data,
    helpful_count: data.helpful_count || 0,
    comment: data.comment || null,
    consultation_id: data.consultation_id || null,
    is_verified: data.is_verified || false,
    created_at: data.created_at || new Date().toISOString(),
    updated_at: data.updated_at || new Date().toISOString(),
  };
}

/**
 * Delete a review
 */
export async function deleteReview(reviewId: string): Promise<void> {
  const { error } = await supabase
    .from('lawyer_reviews')
    .delete()
    .eq('id', reviewId);

  if (error) {
    throw new Error(`Error deleting review: ${error.message}`);
  }
}

/**
 * Check if a client has already reviewed a lawyer
 */
export async function hasClientReviewedLawyer(
  clientId: string,
  lawyerId: string,
  consultationId?: string
): Promise<boolean> {
  let query = supabase
    .from('lawyer_reviews')
    .select('id')
    .eq('client_id', clientId)
    .eq('lawyer_id', lawyerId);

  if (consultationId) {
    query = query.eq('consultation_id', consultationId);
  }

  const { data, error } = await query.maybeSingle();

  if (error) {
    console.error('Error checking existing review:', error);
    return false;
  }

  return !!data;
}

/**
 * Get rating breakdown for a lawyer
 */
export async function getLawyerRatingBreakdown(lawyerId: string): Promise<RatingBreakdown[]> {
  const { data, error } = await supabase
    .rpc('get_lawyer_rating_breakdown', { p_lawyer_id: lawyerId });

  if (error) {
    console.error('Error fetching rating breakdown:', error);
    // Return empty breakdown
    return [
      { rating: 5, count: 0, percentage: 0 },
      { rating: 4, count: 0, percentage: 0 },
      { rating: 3, count: 0, percentage: 0 },
      { rating: 2, count: 0, percentage: 0 },
      { rating: 1, count: 0, percentage: 0 },
    ];
  }

  return data || [];
}

/**
 * Mark a review as helpful
 */
export async function markReviewHelpful(reviewId: string, userId: string): Promise<void> {
  // Insert into review_helpful table
  const { error } = await supabase
    .from('review_helpful')
    .insert({ review_id: reviewId, user_id: userId });

  if (error && error.code !== '23505') {
    // Ignore duplicate key error
    throw new Error(`Error marking review as helpful: ${error.message}`);
  }

  // Increment helpful_count (fetch-update since sql template tag is not available)
  const { data: current } = await supabase
    .from('lawyer_reviews')
    .select('helpful_count')
    .eq('id', reviewId)
    .single();
    
  if (current) {
    const { error: updateError } = await supabase
      .from('lawyer_reviews')
      .update({ helpful_count: (current.helpful_count || 0) + 1 })
      .eq('id', reviewId);

    if (updateError) {
      console.error('Error updating helpful count:', updateError);
    }
  }
}

/**
 * Remove helpful mark from a review
 */
export async function unmarkReviewHelpful(reviewId: string, userId: string): Promise<void> {
  // Delete from review_helpful table
  const { error } = await supabase
    .from('review_helpful')
    .delete()
    .eq('review_id', reviewId)
    .eq('user_id', userId);

  if (error) {
    throw new Error(`Error unmarking review as helpful: ${error.message}`);
  }

  // Decrement helpful_count
  const { data: current } = await supabase
    .from('lawyer_reviews')
    .select('helpful_count')
    .eq('id', reviewId)
    .single();
    
  if (current) {
    const newCount = Math.max((current.helpful_count || 0) - 1, 0);
    const { error: updateError } = await supabase
      .from('lawyer_reviews')
      .update({ helpful_count: newCount })
      .eq('id', reviewId);

    if (updateError) {
      console.error('Error updating helpful count:', updateError);
    }
  }
}

/**
 * Check if user has marked review as helpful
 */
export async function hasUserMarkedReviewHelpful(
  reviewId: string,
  userId: string
): Promise<boolean> {
  const { data, error } = await supabase
    .from('review_helpful')
    .select('id')
    .eq('review_id', reviewId)
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.error('Error checking helpful status:', error);
    return false;
  }

  return !!data;
}

/**
 * Get reviews by a specific client
 */
export async function getClientReviews(clientId: string): Promise<Review[]> {
  const { data, error } = await supabase
    .from('lawyer_reviews')
    .select(`
      *,
      lawyer:lawyer_profiles!lawyer_id(
        id,
        bar_number,
        university,
        practice_areas
      )
    `)
    .eq('client_id', clientId)
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(`Error fetching client reviews: ${error.message}`);
  }

  return (data || []).map(review => ({
    ...review,
    helpful_count: review.helpful_count || 0,
    comment: review.comment || null,
    consultation_id: review.consultation_id || null,
    is_verified: review.is_verified || false,
    created_at: review.created_at || new Date().toISOString(),
    updated_at: review.updated_at || new Date().toISOString(),
  }));
}

/**
 * Get lawyer stats including review info
 */
export async function getLawyerStats(lawyerId: string) {
  const breakdown = await getLawyerRatingBreakdown(lawyerId);

  const total_reviews = breakdown.reduce((acc, curr) => acc + curr.count, 0);
  const weighted_sum = breakdown.reduce((acc, curr) => acc + (curr.rating * curr.count), 0);
  const rating_average = total_reviews > 0 ? weighted_sum / total_reviews : 0;

  return {
    rating_average,
    total_reviews,
    rating_breakdown: breakdown,
  };
}

